// `filterState.checkInDate` / `checkOutDate` are shared by the header filter,
// the listing filters, the mobile search sheet and the property detail page.
// Those screens used to disagree on the type: some wrote "YYYY-MM-DD" strings,
// the detail page wrote raw `Date` objects, and `moment(null).format()` could
// write the literal "Invalid date". Readers that did
// `new Date(value + "T00:00:00")` then turned a Date object into an Invalid
// Date, which surfaced as "Invalid Date" in the header.
//
// The canonical stored form is a "YYYY-MM-DD" string (that's also what the API
// expects). Write through `toFilterDateString`, read through `parseFilterDate`.

export type FilterDate = string | Date | null | undefined;

const YMD = /^\d{4}-\d{2}-\d{2}$/;

const startOfDay = (d: Date) => {
  const copy = new Date(d.getTime());
  copy.setHours(0, 0, 0, 0);
  return copy;
};

/**
 * Accepts whatever is currently sitting in filterState (or a URL param) and
 * returns a local-midnight Date, or null when it can't be understood.
 */
export const parseFilterDate = (value: FilterDate): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : startOfDay(value);
  }

  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  // These all reach us for real: "null"/"undefined" from template-interpolated
  // URL params, "Invalid date" from an unguarded moment().format().
  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed.toLowerCase() === "invalid date"
  ) {
    return null;
  }

  // A bare "YYYY-MM-DD" is parsed as UTC, which lands on the previous day in
  // negative offsets — pin it to local midnight instead.
  const parsed = YMD.test(trimmed)
    ? new Date(`${trimmed}T00:00:00`)
    : new Date(trimmed);

  return isNaN(parsed.getTime()) ? null : startOfDay(parsed);
};

/**
 * Normalises any of the above into the canonical "YYYY-MM-DD" string, or null.
 * Use this for every write into filterState and for building detail-page URLs.
 */
export const toFilterDateString = (value: FilterDate): string | null => {
  const date = parseFilterDate(value);
  if (!date) return null;

  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};
